<script lang="ts">
import FindCompanyEconomicSectorOptions from "@/Components/FindCompany/FindCompanyEconomicSectorOptions.vue";
import FindCompanySkills from "@/Components/FindCompany/FindCompanySkills.vue";
import GenericButton from "@/Components/Generics/GenericButton.vue";
import Plane from "@/Components/Generics/Plane.vue";
import { EconomicSectors, QualificationId, QualificationInput, QualificationsGroups, SelectedEconomicSectorIds } from "@/Types";
import { Policies } from "@/Types/acp";
import { Company } from "@/Types/company";
import { Inertia } from "@inertiajs/inertia";
import axios from "axios";
import { PropType, defineComponent } from "vue";

export default defineComponent({
    components: {
        Plane,
        FindCompanySkills,
        FindCompanyEconomicSectorOptions,
        GenericButton,
    },
    props: {
        qualificationGroups: {
            type: Object as PropType<QualificationsGroups>,
            required: true,
        },
        economicSectors: {
            type: Object as PropType<EconomicSectors>,
            required: true,
        },

        company: {
            type: Object as PropType<Company>,
            default: null,
        },
        policies: {
            type: Object as PropType<Policies>,
            default: null,
        },
        imageSize: {
            type: Number,
            default: 2,
        },

        maxValue: {
            type: Number,
            default: 5,
        },
        emitActions: {
            type: Boolean,
            default: false,
        },
        showActiveCheckbox: {
            type: Boolean,
            default: true,
        },
        create: {
            type: Boolean,
            default: false,
        },
        copy: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["submit"],
    data() {
        return {
            formCompany: { ...this.company },

            qualificationInput: [] as QualificationInput,
            imageInput: null as File,
            imageContactInput: null as File,
            success: "",
            processing: false,
            readonly: false,
            submitErrors: [] as string[],
            imageErrors: { logo: [] as string[], contact: [] as string[] },
        };
    },
    computed: {
        formCompanySectorIds(): SelectedEconomicSectorIds {
            const sectors = this.formCompany.sectors;
            if (!sectors) return [];
            return sectors.map((sector) => sector.id);
        },
        submitButtonChoice(): number {
            if (this.copy) {
                return 3;
            } else if (this.create) {
                return 2;
            } else {
                return 1;
            }
        },
    },
    methods: {
        onChangeImageInput(e: Event): void {
            const target = e.target as HTMLInputElement;
            this.imageInput = target.files?.[0] ?? null;
        },
        onChangeImageContactInput(e: Event): void {
            const target = e.target as HTMLInputElement;
            this.imageContactInput = target.files?.[0] ?? null;
        },

        imageUpload(contact: boolean): void {
            const file = contact ? this.imageContactInput : this.imageInput;
            if (!file || !this.formCompany.id) return;

            const config = {
                headers: {
                    "content-type": "multipart/form-data",
                },
            };

            const data = new FormData();
            data.append("image", file);
            data.append("companyId", String(this.company.id));
            data.append("contact", contact ? "1" : "0");

            this.imageErrors[contact ? "contact" : "logo"] = [];
            axios
                .post(route("company/image/create"), data, config)
                .then((res) => {
                    this.success = res.data.success;
                    Inertia.reload();
                })
                .catch((err) => {
                    this.imageErrors[contact ? "contact" : "logo"] = this.parseSubmitError(err);
                });
        },
        imageDelete(contact: boolean): void {
            if (!this.formCompany.id || !confirm(this.$trans("company.image_delete_confirm"))) return;

            this.imageErrors[contact ? "contact" : "logo"] = [];
            axios
                .post(route("company/image/delete"), { companyId: this.formCompany.id, contact: contact ? 1 : 0 })
                .then(() => {
                    if (contact) this.formCompany.image_contact = null;
                    else this.formCompany.image = null;
                    Inertia.reload();
                })
                .catch((err) => {
                    this.imageErrors[contact ? "contact" : "logo"] = this.parseSubmitError(err);
                });
        },
        submit(preserveState = false, copy: boolean = false): void {
            if (this.processing) return;

            this.processing = true;
            this.submitErrors = [];

            const data = {
                ...this.formCompany,
                // requirements: this.qualificationInput,
                sectors: this.formCompany.sectors?.map((s) => s.id) ?? [],
            };

            if (this.emitActions) {
                this.$emit(
                    "submit",
                    data,
                    (error?: unknown) => {
                        this.processing = false;
                        if (error) this.submitErrors = this.parseSubmitError(error);
                    },
                    preserveState,
                    copy
                );
            } else {
                axios
                    .post(route("company/store"), data)
                    .then((response) => {
                        this.processing = false;
                        this.$inertia.visit(route("company/create"), { preserveState: false, data: { id: response.data.id } });
                    })
                    .catch((error) => {
                        this.processing = false;
                        this.submitErrors = this.parseSubmitError(error);
                    });
            }
        },
        parseSubmitError(error: unknown): string[] {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 422 && error.response.data?.errors) {
                    return Object.values(error.response.data.errors as Record<string, string[]>).flat();
                }
                if (error.response?.status === 403) {
                    return [this.$trans("company.error_forbidden")];
                }
            }
            return [this.$trans("company.error_save")];
        },
        clickInertiaLink(inertiaRoute: string): void {
            this.$inertia.visit(route(inertiaRoute));
        },

        handleQualificationValueChangedEvent(qualificationId: QualificationId, value: number, important?: 1 | 0): void {
            if (!this.formCompany.requirements) this.formCompany.requirements = [];

            const requirements = this.formCompany.requirements;

            const requirementByQualificationId = requirements.findIndex((r) => r.qualification_id === qualificationId);

            const newReq = { id: qualificationId, value: value, important: important, qualification_id: qualificationId };

            if (requirementByQualificationId < 0) {
                requirements.push(newReq);
            } else {
                requirements[requirementByQualificationId] = newReq;
            }

            this.formCompany.requirements = requirements;

            // let qualificationInputToRemove = this.qualificationInput.map((qualificationInput) => qualificationInput.id);
            // let index = qualificationInputToRemove.indexOf(qualificationId);
            // if (index >= 0) this.qualificationInput.splice(index, 1);
            // this.qualificationInput.push({ id: qualificationId, value: value, important: important });
        },
        handleSectorsChanged(selectedSectors: SelectedEconomicSectorIds): void {
            this.formCompany.sectors = selectedSectors.map((sectorId) => this.economicSectors.find((sector) => sector.id === sectorId));
        },
    },
});
</script>

<template>
    <div class="flex flex--width flex--v flex--valign-c flex--gap-50">
        <!-- BUTTONS FROM BOTTOM -->
        <div class="btn-group" role="group" aria-label="button group">
            <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(false)" />
            <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit_and_continue', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(true)" />
            <GenericButton v-if="!create" class="btn-group-button" :title="$transChoice('company.button_submit', 3)" :secondary="true" :disabled="processing || !formCompany.name" @click="submit(false, true)" />
        </div>
        <div class="btn-group-vertical" role="group" aria-label="Vertical button group">
            <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(false)" />
            <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit_and_continue', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(true)" />
            <GenericButton v-if="!create" class="btn-group-button" :title="$transChoice('company.button_submit', 3)" :secondary="true" :disabled="processing || !formCompany.name" @click="submit(false, true)" />
        </div>

        <!-- GENERAL INFO -->
        <Plane :darker="true" :rounded="true">
            <div class="flex flex--width flex--v flex--gap-30">
                <!-- SECTION TITLE -->
                <span class="text text--primary-b text--size-27 text--medium text--height-22 text--left">
                    {{ $trans("acp.company_create_1") }}
                </span>

                <!-- INFO -->
                <div class="flex flex--width flex--v">
                    <label for="name" class="form-label">{{ $trans("company.name") + "*" }}</label>
                    <input id="name" v-model="formCompany.name" type="text" class="form-control" aria-describedby="nameHelp" :readonly="readonly" />
                    <div id="nameHelp" class="form-text">
                        {{ $trans("company.name_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v">
                    <label for="description" class="form-label">{{ $trans("company.description") }}</label>
                    <textarea id="description" v-model="formCompany.description" type="text" class="form-control" aria-describedby="descriptionHelp" rows="5" :readonly="readonly" />
                    <div id="descriptionHelp" class="form-text">
                        {{ $trans("company.description_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v">
                    <label for="email" class="form-label">{{ $trans("company.email") }}</label>
                    <input id="email" v-model="formCompany.email" type="email" class="form-control" aria-describedby="emailHelp" :readonly="readonly" />
                    <div id="emailHelp" class="form-text">
                        {{ $trans("company.email_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v">
                    <label for="phone" class="form-label">{{ $trans("company.phone") }}</label>
                    <input id="phone" v-model="formCompany.phone" type="text" class="form-control" aria-describedby="phoneHelp" :readonly="readonly" />
                    <div id="phoneHelp" class="form-text">
                        {{ $trans("company.phone_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v">
                    <label for="website" class="form-label">{{ $trans("company.website") }}</label>
                    <input id="website" v-model="formCompany.website" type="text" class="form-control" aria-describedby="websiteHelp" :readonly="readonly" />
                    <div id="websiteHelp" class="form-text">
                        {{ $trans("company.website_help") }}
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col-lg-10">
                        <label for="streetName" class="form-label">{{ $trans("company.streetName") }}</label>
                        <input id="streetName" v-model="formCompany.street_name" type="text" class="form-control" :readonly="readonly" />
                    </div>
                    <div class="col-lg-2">
                        <label for="streetNumber" class="form-label">{{ $trans("company.streetNumber") }}</label>
                        <input id="streetNumber" v-model="formCompany.street_number" type="number" class="form-control" :readonly="readonly" />
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col-lg-9">
                        <label for="city" class="form-label">{{ $trans("company.city") }}</label>
                        <input id="city" v-model="formCompany.city" type="text" class="form-control" :readonly="readonly" />
                    </div>
                    <div class="col-lg-3">
                        <label for="zipcode" class="form-label">{{ $trans("company.zipcode") }}</label>
                        <input id="zipcode" v-model="formCompany.zipcode" type="number" class="form-control" :readonly="readonly" />
                    </div>
                </div>
            </div>
        </Plane>

        <!-- AUSBILDUNGSANGEBOT -->
        <Plane :darker="true" :rounded="true">
            <div class="flex flex--width flex--v flex--gap-30">
                <!-- SECTION TITLE -->
                <span class="text text--primary-b text--size-27 text--medium text--height-22 text--left">
                    {{ $trans("acp.company_create_2") }}
                </span>

                <!-- INFO -->
                <div class="flex flex--width flex--v">
                    <label for="occupation" class="form-label">{{ $trans("company.occupation") }}</label>
                    <input id="occupation" v-model="formCompany.occupation" type="text" class="form-control" aria-describedby="occupationHelp" :readonly="readonly" />
                    <div id="occupationHelp" class="form-text">
                        {{ $trans("company.occupation_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v">
                    <label class="form-label">{{ $trans("company.economic_sectors") }}</label>
                    <div class="text text--size-14 text--medium">
                        {{ $trans("company.economic_sectors_help") }}
                    </div>
                </div>

                <FindCompanyEconomicSectorOptions :sectors="economicSectors" :selected-sectors="formCompanySectorIds" @sector-value-changed="handleSectorsChanged" />
            </div>
        </Plane>

        <!-- VORAUSSETZUNGEN & KOMPETENZEN -->
        <Plane :darker="true" :rounded="true">
            <div class="flex flex--width flex--v flex--gap-30">
                <!-- SECTION TITLE -->
                <span class="text text--primary-b text--size-27 text--medium text--height-22 text--left">
                    {{ $trans("acp.company_create_3") }}
                </span>

                <!-- INFO -->
                <div class="flex flex--width flex--v">
                    <label for="educational_requirements" class="form-label">{{ $trans("company.educational_requirements") }}</label>
                    <input id="educational_requirements" v-model="formCompany.educational_requirements" type="text" class="form-control" aria-describedby="educationalRequirementsHelp" :readonly="readonly" />
                    <div id="educationalRequirementsHelp" class="form-text">
                        {{ $trans("company.educational_requirements_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v">
                    <label class="form-label">{{ $trans("company.qualification_title") }}</label>
                    <div class="flex flex--v flex--gap-10">
                        <div class="text text--size-14 text--medium">
                            {{ $trans("company.qualification_help") }}
                        </div>
                        <div class="text text--size-14 text--medium">
                            {{ $trans("company.qualification_specific") }}
                        </div>
                    </div>
                </div>

                <FindCompanySkills :qualification-groups="qualificationGroups" :company-requirements="formCompany.requirements" :create="true" @create-value-changed="handleQualificationValueChangedEvent" />
            </div>
        </Plane>

        <!-- BEWERBUNG & ANSPRECHPARTNER -->
        <Plane :darker="true" :rounded="true">
            <div class="flex flex--width flex--v flex--gap-30">
                <!-- SECTION TITLE -->
                <span class="text text--primary-b text--size-27 text--medium text--height-22 text--left">
                    {{ $trans("acp.company_create_4") }}
                </span>

                <!-- INFO -->
                <div class="flex flex--width flex--v">
                    <label for="contact" class="form-label">{{ $trans("company.contact") }}</label>
                    <input id="contact" v-model="formCompany.contact" type="text" class="form-control" aria-describedby="contactHelp" :readonly="readonly" />
                    <div id="contactHelp" class="form-text">
                        {{ $trans("company.contact_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v flex--gap-10">
                    <div class="flex flex--width flex--v">
                        <label for="imageContactUpload" class="form-label">
                            {{
                                $trans("company.image_contact", {
                                    args: { imageSize },
                                })
                            }}
                        </label>
                        <input
                            id="imageContactUpload"
                            class="form-control"
                            type="file"
                            accept=".jpeg,.png,.jpg,.gif,.bmp,.webp"
                            :disabled="formCompany.id === undefined"
                            aria-describedby="imageContactHelp"
                            :readonly="readonly"
                            @change="onChangeImageContactInput" />
                        <div v-if="formCompany.id === undefined" id="imageContactHelp" class="form-text">
                            {{ $trans("company.image_contact_disabled") }}
                        </div>
                    </div>
                    <div class="flex flex--width flex--halign-s">
                        <GenericButton :title="$trans('company.upload')" :circular="true" :disabled="!imageContactInput || !formCompany.id" @click="imageUpload(true)" />
                    </div>
                    <ul v-if="imageErrors.contact.length > 0" class="text text--color-red" role="alert">
                        <li v-for="(error, index) in imageErrors.contact" :key="index">{{ error }}</li>
                    </ul>
                </div>

                <div v-if="formCompany.image_contact" class="flex flex--width flex--v flex--valign-c flex--gap-10">
                    <div class="company-image">
                        <img :src="formCompany.image_contact.imageUrl" class="img-fluid" />
                    </div>
                    <GenericButton :title="$trans('company.image_delete')" :circular="true" :secondary="true" :disabled="readonly" @click="imageDelete(true)" />
                </div>

                <div class="flex flex--width flex--v">
                    <label for="internshipTime" class="form-label">{{ $trans("company.internship_time") }}</label>
                    <input id="internshipTime" v-model="formCompany.internship_time" type="text" class="form-control" aria-describedby="internshipTimeHelp" :readonly="readonly" maxlength="100" />
                    <div id="internshipTimeHelp" class="form-text">
                        {{ $trans("company.internship_time_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v">
                    <label for="internshipScope" class="form-label">{{ $trans("company.internship_scope") }}</label>
                    <input id="internshipScope" v-model="formCompany.internship_scope" type="text" class="form-control" aria-describedby="internshipScopeHelp" :readonly="readonly" maxlength="250" />
                    <div id="internshipScopeHelp" class="form-text">
                        {{ $trans("company.internship_scope_help") }}
                    </div>
                </div>
            </div>
        </Plane>

        <!-- EXTRAS -->
        <Plane :darker="true" :rounded="true">
            <div class="flex flex--width flex--v flex--gap-30">
                <!-- SECTION TITLE -->
                <span class="text text--primary-b text--size-27 text--medium text--height-22 text--left">
                    {{ $trans("acp.company_create_5") }}
                </span>

                <!-- INFO -->
                <div class="flex flex--width flex--v">
                    <label for="internshipExtras" class="form-label">{{ $trans("company.internship_extras") }}</label>
                    <input id="internshipExtras" v-model="formCompany.internship_extras" type="text" class="form-control" aria-describedby="internshipExtrasHelp" :readonly="readonly" maxlength="2000" />
                    <div id="internshipExtrasHelp" class="form-text">
                        {{ $trans("company.internship_extras_help") }}
                    </div>
                </div>

                <div class="flex flex--width flex--v flex--gap-10">
                    <div class="flex flex--width flex--v">
                        <label for="imageUpload" class="form-label">{{ $trans("company.image", { args: { imageSize } }) }}</label>
                        <input
                            id="imageUpload"
                            class="form-control"
                            type="file"
                            accept=".jpeg,.png,.jpg,.gif,.bmp,.webp"
                            :disabled="formCompany.id === undefined"
                            aria-describedby="imageHelp"
                            :readonly="readonly"
                            @change="onChangeImageInput" />
                        <div v-if="formCompany.id === undefined" id="imageHelp" class="form-text">
                            {{ $trans("company.image_disabled") }}
                        </div>
                    </div>
                    <div class="flex flex--width flex--halign-s">
                        <GenericButton :title="$trans('company.upload')" :circular="true" :disabled="!imageInput || !formCompany.id" @click="imageUpload(false)" />
                    </div>
                    <ul v-if="imageErrors.logo.length > 0" class="text text--color-red" role="alert">
                        <li v-for="(error, index) in imageErrors.logo" :key="index">{{ error }}</li>
                    </ul>
                </div>

                <div v-if="formCompany.image" class="flex flex--width flex--v flex--valign-c flex--gap-10">
                    <div class="company-image">
                        <img :src="formCompany.image.imageUrl" class="img-fluid" />
                    </div>
                    <GenericButton :title="$trans('company.image_delete')" :circular="true" :secondary="true" :disabled="readonly" @click="imageDelete(false)" />
                </div>
            </div>
        </Plane>

        <div v-if="submitErrors.length > 0" class="flex flex--v flex--valign-s flex--gap-15" role="alert">
            <div class="text text--color-red text--size-18 text--semi-bold text--center">{{ $trans("company.error_title") }}</div>
            <ul class="text text--accent-gray-b text--size-18 text--semi-bold text--center">
                <li v-for="(error, index) in submitErrors" :key="index">{{ error }}</li>
            </ul>
        </div>

        <Plane v-if="showActiveCheckbox" :darker="true" :rounded="true">
            <div class="flex flex--width flex--v flex--valign-c flex--gap-30">
                <div v-if="showActiveCheckbox" class="full-width mb-3 form-check">
                    <input
                        id="active"
                        :checked="formCompany.active === 1"
                        type="checkbox"
                        class="form-check-input"
                        aria-describedby="activeHelp"
                        :disabled="!!policies && !policies.COMPANY_ACTIVE"
                        @input="formCompany.active = formCompany.active === 1 ? 0 : 1" />
                    <label class="form-check-label" for="active">{{ $trans("company.active") }}</label>
                    <div id="activeHelp" class="form-text">
                        {{ !!policies && !policies.COMPANY_ACTIVE ? $trans("company.active_help_customer") : $trans("company.active_help") }}
                    </div>
                </div>

                <div class="bottom-buttons flex flex--v flex--halign-c flex--valign-c flex--gap-20">
                    <div class="btn-group" role="group" aria-label="button group">
                        <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(false)" />
                        <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit_and_continue', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(true)" />
                        <GenericButton v-if="!create" class="btn-group-button" :title="$transChoice('company.button_submit', 3)" :secondary="true" :disabled="processing || !formCompany.name" @click="submit(false, true)" />
                    </div>
                    <div class="btn-group-vertical" role="group" aria-label="Vertical button group">
                        <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(false)" />
                        <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit_and_continue', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(true)" />
                        <GenericButton v-if="!create" class="btn-group-button" :title="$transChoice('company.button_submit', 3)" :secondary="true" :disabled="processing || !formCompany.name" @click="submit(false, true)" />
                    </div>

                    <div class="btn-group" role="group" aria-label="button group">
                        <GenericButton class="btn-group-button" :title="$trans('company.button_reset')" :simple="true" @click="clickInertiaLink('companies.create')" />
                        <GenericButton class="btn-group-button" :title="$trans('company.button_cancel')" :danger="true" @click="clickInertiaLink('companies.index')" />
                    </div>
                    <div class="btn-group-vertical" role="group" aria-label="Vertical button group">
                        <GenericButton class="btn-group-button" :title="$trans('company.button_reset')" :simple="true" @click="clickInertiaLink('companies.create')" />
                        <GenericButton class="btn-group-button" :title="$trans('company.button_cancel')" :danger="true" @click="clickInertiaLink('companies.index')" />
                    </div>
                </div>
            </div>
        </Plane>
        <div v-else class="bottom-buttons flex flex--v flex--halign-c flex--valign-c flex--gap-20">
            <div class="btn-group" role="group" aria-label="button group">
                <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(false)" />
                <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit_and_continue', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(true)" />
                <GenericButton v-if="!create" class="btn-group-button" :title="$transChoice('company.button_submit', 3)" :secondary="true" :disabled="processing || !formCompany.name" @click="submit(false, true)" />
            </div>
            <div class="btn-group-vertical" role="group" aria-label="Vertical button group">
                <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(false)" />
                <GenericButton class="btn-group-button" :title="$transChoice('company.button_submit_and_continue', submitButtonChoice)" :disabled="processing || !formCompany.name" @click="submit(true)" />
                <GenericButton v-if="!create" class="btn-group-button" :title="$transChoice('company.button_submit', 3)" :secondary="true" :disabled="processing || !formCompany.name" @click="submit(false, true)" />
            </div>

            <div class="btn-group" role="group" aria-label="button group">
                <GenericButton class="btn-group-button" :title="$trans('company.button_reset')" :simple="true" @click="clickInertiaLink('companies.create')" />
                <GenericButton class="btn-group-button" :title="$trans('company.button_cancel')" :danger="true" @click="clickInertiaLink('companies.index')" />
            </div>
            <div class="btn-group-vertical" role="group" aria-label="Vertical button group">
                <GenericButton class="btn-group-button" :title="$trans('company.button_reset')" :simple="true" @click="clickInertiaLink('companies.create')" />
                <GenericButton class="btn-group-button" :title="$trans('company.button_cancel')" :danger="true" @click="clickInertiaLink('companies.index')" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.company-image {
    max-width: min(60%, 60vw);
    max-height: min(60%, 60vw);
}
.btn-group {
    gap: rem(10);
}
.btn-group-vertical {
    display: none;
    gap: rem(5);

    .btn-group-button {
        width: 100%;
    }
}

@include media-breakpoint-down(xl) {
    .bottom-buttons {
        width: fit-content;
    }

    .btn-group {
        display: none;
    }
    .btn-group-vertical {
        display: flex;
    }
}
@include media-breakpoint-down(md) {
    .company-image {
        max-width: min(70%, 70vw);
        max-height: min(70%, 70vw);
    }
}
@include media-breakpoint-down(sm) {
    .company-image {
        max-width: min(70%, 70vw);
        max-height: min(70%, 70vw);
    }
}
</style>
